import React, { useEffect, useState, useCallback } from 'react';
import './super-admin.css';
import { Typography, Card, List, Tag, Space, Button, Input, message, Row, Col, Select, DatePicker, Statistic, Popconfirm } from 'antd';
import { FileTextOutlined, DownloadOutlined, DeleteOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../config/api';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const levelColor = (lvl) => ({ 
  INFO: 'blue', 
  WARN: 'gold', 
  ERROR: 'red',
  SUCCESS: 'green'
}[lvl] || 'default');

const LogsAndActions = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [stats, setStats] = useState({ total: 0, errors: 0, warnings: 0, info: 0 });

  // Load system logs from API
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '100',
        offset: '0'
      });
      
      if (levelFilter) params.append('level', levelFilter);
      if (query) params.append('search', query);
      if (dateRange && dateRange[0]) params.append('from', dateRange[0].toISOString());
      if (dateRange && dateRange[1]) params.append('to', dateRange[1].toISOString());
      
      const res = await fetch(`${apiBase}/admin/system-logs?${params}`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load system logs');
      const data = await res.json();
      
      setLogs(data.logs || []);
      
      // Calculate stats
      const newStats = {
        total: data.total || 0,
        errors: data.logs?.filter(log => log.level === 'ERROR').length || 0,
        warnings: data.logs?.filter(log => log.level === 'WARN').length || 0,
        info: data.logs?.filter(log => log.level === 'INFO').length || 0
      };
      setStats(newStats);
      
    } catch (err) {
      message.error(err.message || 'Error loading system logs');
    } finally {
      setLoading(false);
    }
  }, [query, levelFilter, dateRange]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Export logs
  const exportLogs = async () => {
    try {
      const res = await fetch(`${apiBase}/admin/system-logs/export`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to export logs');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `system-logs-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('System logs exported successfully!');
    } catch (err) {
      message.error(err.message || 'Error exporting logs');
    }
  };

  // Clear old logs
  const clearOldLogs = async () => {
    try {
      const res = await fetch(`${apiBase}/admin/system-logs/cleanup?days=30`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to cleanup logs');
      const result = await res.json();
      
      message.success(result.message);
      loadLogs(); // Refresh the logs
    } catch (err) {
      message.error(err.message || 'Error cleaning up logs');
    }
  };

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 24 }}>System Logs & Actions</Title>

      <Row gutter={[16, 16]}>
        {/* Statistics Cards */}
        <Col xs={24} lg={18}>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ borderRadius: 14 }}>
                <Statistic
                  title="Total Logs"
                  value={stats.total}
                  prefix={<FileTextOutlined style={{ color: '#3730a3' }} />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ borderRadius: 14 }}>
                <Statistic
                  title="Errors"
                  value={stats.errors}
                  valueStyle={{ color: '#dc2626' }}
                  prefix={<FileTextOutlined style={{ color: '#dc2626' }} />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ borderRadius: 14 }}>
                <Statistic
                  title="Warnings"
                  value={stats.warnings}
                  valueStyle={{ color: '#d97706' }}
                  prefix={<FileTextOutlined style={{ color: '#d97706' }} />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ borderRadius: 14 }}>
                <Statistic
                  title="Info"
                  value={stats.info}
                  valueStyle={{ color: '#2563eb' }}
                  prefix={<FileTextOutlined style={{ color: '#2563eb' }} />}
                />
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Actions Card */}
        <Col xs={24} lg={6}>
          <Card 
            hoverable 
            style={{ borderRadius: 14, height: '100%' }} 
            bodyStyle={{ padding: 20 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ 
                width: 44, 
                height: 44, 
                borderRadius: 10, 
                background: '#05966922', 
                color: '#059669', 
                display: 'grid', 
                placeItems: 'center', 
                fontSize: 20 
              }}>
                <FilterOutlined />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>Quick Actions</div>
                <div style={{ color: '#64748b', fontSize: 13 }}>Manage system logs</div>
              </div>
            </div>

            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={loadLogs} 
                loading={loading}
                style={{ width: '100%' }}
              >
                Refresh Logs
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={exportLogs}
                style={{ width: '100%' }}
              >
                Export Logs
              </Button>
              <Popconfirm
                title="Clean up old logs?"
                description="This will delete logs older than 30 days."
                onConfirm={clearOldLogs}
                okText="Yes, Clean Up"
                cancelText="Cancel"
                okType="danger"
              >
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  style={{ width: '100%' }}
                >
                  Clean Old Logs
                </Button>
              </Popconfirm>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Filters and Logs */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card 
            hoverable 
            style={{ borderRadius: 14 }} 
            bodyStyle={{ padding: 20 }}
            loading={loading}
          >
            {/* Header with filters */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: 10, 
                  background: '#3730a322', 
                  color: '#3730a3', 
                  display: 'grid', 
                  placeItems: 'center', 
                  fontSize: 20 
                }}>
                  <FileTextOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>System Logs</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Real-time system activity logs</div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              flexWrap: 'wrap',
              padding: '12px 16px', 
              background: '#f8fafc', 
              borderRadius: 8, 
              marginBottom: 16 
            }}>
              <Input.Search
                allowClear
                placeholder="Search logs..."
                style={{ minWidth: 250, flex: 1 }}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onSearch={loadLogs}
              />
              <Select
                placeholder="Filter by level"
                style={{ minWidth: 120 }}
                value={levelFilter}
                onChange={setLevelFilter}
                allowClear
              >
                <Select.Option value="INFO">Info</Select.Option>
                <Select.Option value="WARN">Warning</Select.Option>
                <Select.Option value="ERROR">Error</Select.Option>
              </Select>
              <RangePicker
                style={{ minWidth: 250 }}
                value={dateRange}
                onChange={setDateRange}
                showTime
              />
            </div>

            {/* Logs List */}
            <div style={{ 
              maxHeight: '500px', 
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: 8
            }}>
              <List
                dataSource={logs}
                renderItem={(log) => (
                  <List.Item style={{ 
                    padding: '12px 16px', 
                    borderBottom: '1px solid #f1f5f9',
                    margin: 0
                  }}>
                    <div style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                        <Tag color={levelColor(log.level)} style={{ margin: 0 }}>
                          {log.level}
                        </Tag>
                        <span style={{ color: '#64748b', fontSize: 12 }}>
                          {log.formatted_time}
                        </span>
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>
                          [{log.source}]
                        </span>
                      </div>
                      <div style={{ color: '#1f2937', fontSize: 14 }}>
                        {log.text}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
              {logs.length === 0 && !loading && (
                <div style={{ 
                  padding: 32, 
                  textAlign: 'center',
                  color: '#64748b' 
                }}>
                  <FileTextOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                  <div>No logs to display</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    Try adjusting your filters or check back later
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LogsAndActions;
